// src/pages/Community/Vehicle/VehicleDetail.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import "./css/VehicleWeaponDetail.css";
import VehicleDetailContent from "./VehicleDetailContent";
import { normalizeVehicle } from "./normalizeVehicle";
// ---------- Utils ----------
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

export default function VehicleDetail() {
  const { id } = useParams();
  const location = useLocation();

  // state & hooks (declared unconditionally)
  const [v, setV] = useState(undefined); // undefined = loading, null = not found
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  // immediate UI reaction feedback
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  const basePath = useMemo(() => ["vehicles", id], [id]);
  const bookmarkKey = useMemo(() => `vehicles:${id}`, [id]);

  // ----- Data -----
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "vehicles", id), (snap) => {
      if (!snap.exists()) return setV(null);
      const raw = snap.data();
      setV({ id: snap.id, ...normalizeVehicle(raw), _raw: raw });
    });
    return () => unsub();
  }, [id]);

  const yt = useMemo(() => (v?.videoUrl ? toYouTubeEmbed(v.videoUrl) : null), [v?.videoUrl]);

  // ----- Share/Copy -----
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) {
        await navigator.share({ title: v?.name ?? "Vehicle", url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch { }
  }, [v?.name, location.pathname, location.hash]);

  // ----- Lightbox helpers -----
  const openLightbox = useCallback((index) => {
    setLightbox({ open: true, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
  }, []);

  const nextLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = v?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index + 1) % len : 0 };
    });
  }, [v?.gallery?.length]);

  const prevLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = v?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
    });
  }, [v?.gallery?.length]);

  // keyboard nav
  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight, closeLightbox]);

  // ----- Loading / 404 -----
  if (v === undefined) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-skel-card" aria-busy="true">
            <div className="vw-skel-hero" />
            <div className="vw-skel-title" />
            <div className="vw-skel-row" />
            <div className="vw-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }
  if (v === null) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-empty-card" role="alert">
            <div className="vw-empty-title">We can’t find that vehicle</div>
            <p className="vw-empty-sub">It may have been removed or the link is incorrect.</p>
            <Link to="vehicles-weapons?tab=vehicles" className="vw-btn vw-btn--primary">← Back to Vehicles</Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ----- Render -----
  return (
    <PageWrapper>
      <div className="vw-wrap">
        {/* Top bar */}
        <div className="vw-toprow">
          <Link to="/vehicles-weapons?tab=vehicles" className="vw-back" aria-label="Back">
            ← All vehicles
          </Link>
          <div className="vw-actions">
            {v.videoUrl && !yt && (
              <a className="vw-action" href={v.videoUrl} target="_blank" rel="noreferrer">
                ▶ Open video
              </a>
            )}
            <button className="vw-action" onClick={onShare}>⤴ Share</button>
          </div>
        </div>

        {/* ✅ MAIN CONTENT */}
        <VehicleDetailContent
          v={v}
          entityId={id}
          basePath={basePath}
          bookmarkKey={bookmarkKey}
          yt={yt}
          openLightbox={openLightbox}
          selectedReactionFromBar={selectedReactionFromBar}
          setSelectedReactionFromBar={setSelectedReactionFromBar}
        />
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(v.gallery) && v.gallery.length > 0 && (
        <div
          ref={lightboxRef}
          className="vw-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button className="vw-lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
          <button className="vw-lightbox-nav left" onClick={prevLight} aria-label="Previous">‹</button>
          <img
            className="vw-lightbox-img"
            src={v.gallery[lightbox.index]}
            alt={`Image ${lightbox.index + 1}`}
          />
          <button className="vw-lightbox-nav right" onClick={nextLight} aria-label="Next">›</button>
        </div>
      )}
    </PageWrapper>

  );
}
